import { ReactElement, useMemo, useState } from 'react';
import {
    BarChart,
    Bar,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    Cell,
    CartesianGrid,
    TooltipContentProps
} from 'recharts';
import { ChartContainer } from './chart-container';
import { blue, fontColorLight, moonlight, opacity, red } from '@/ui/styles';
import { useFormatDate } from '@/common/hooks';
import { useAnalyticsTranslation } from '../hooks';
import { useTasksAndAlertsAnalytics } from '../providers/tasks-and-alerts-analytics-provider';
import { UIText } from '@/ui';
import { CategoricalChartFunc } from 'recharts/types/chart/types';

const INACTIVE_BAR_OPACITY = 0.3;
const CORNER_RADIUS = 8;

function CustomTooltip(
    props: TooltipContentProps<number, string>
): ReactElement {
    const { active, payload } = props;
    if (!active || !payload || payload.length === 0) {
        return <></>;
    }

    type ChartData = {
        taskCount: number;
        alertCount: number;
        start: Date;
        end: Date;
    };
    const data = (payload[0] as { payload: ChartData }).payload;
    const taskCount = data.taskCount ?? 0;
    const alertCount = data.alertCount ?? 0;
    const start = data.start;
    const end = data.end;

    return (
        <TooltipContent
            taskCount={taskCount}
            alertCount={alertCount}
            start={start}
            end={end}
        />
    );
}

function TooltipContent(props: {
    taskCount: number;
    alertCount: number;
    start: Date;
    end: Date;
}): ReactElement {
    const { taskCount, alertCount, start, end } = props;
    const { t } = useAnalyticsTranslation();
    const { formatDateOnly } = useFormatDate();
    const dateString = useMemo(() => {
        const items = [];
        if (start) {
            items.push(formatDateOnly(start));
        }
        if (end) {
            items.push(formatDateOnly(end));
        }
        return items.join(' - ');
    }, [formatDateOnly, start, end]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'white',
                borderRadius: 8,
                border: `1px solid ${moonlight}`,
                gap: 8,
                padding: 8
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                }}
            >
                <div
                    style={{
                        width: 12,
                        height: 12,
                        borderRadius: 12,
                        backgroundColor: blue
                    }}
                />
                <UIText>
                    {t('shared.charts.tasks_value', { value: taskCount })}
                </UIText>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                    style={{
                        width: 12,
                        height: 12,
                        borderRadius: 12,
                        backgroundColor: red
                    }}
                />
                <UIText>
                    {t('shared.charts.alerts_value', { value: alertCount })}
                </UIText>
            </div>
            <UIText type="default2" variant="small">
                {dateString}
            </UIText>
        </div>
    );
}

export function TasksAndAlertsBarChart(): ReactElement {
    const { t } = useAnalyticsTranslation();
    const { dateString, tasksAndAlertsData, status } =
        useTasksAndAlertsAnalytics();
    const { formatDateOnly } = useFormatDate();

    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const handleMouseMove: CategoricalChartFunc = (state) => {
        const { isTooltipActive, activeTooltipIndex } = state;
        if (!isTooltipActive) {
            setActiveIndex(null);
        } else if (
            (typeof activeTooltipIndex === 'number' && activeTooltipIndex) ||
            activeTooltipIndex === 0
        ) {
            setActiveIndex(activeTooltipIndex);
        }
    };

    const handleMouseLeave = () => {
        setActiveIndex(null);
    };

    const getBarOpacity = (index: number): number => {
        if (activeIndex === null || index === activeIndex) {
            return 1;
        } else {
            return INACTIVE_BAR_OPACITY;
        }
    };

    return (
        <ChartContainer
            title={t('components.tasks_and_alerts_bar_chart.title')}
            titleWeight="medium"
            subtitle={dateString ?? '...'}
            description={t('components.tasks_and_alerts_bar_chart.description')}
            loading={status === 'pending'}
            error={status === 'error'}
        >
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={tasksAndAlertsData}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                >
                    <CartesianGrid
                        horizontal={true}
                        vertical={false}
                        stroke={moonlight}
                    />

                    <XAxis
                        dataKey="start"
                        axisLine={{
                            stroke: moonlight
                        }}
                        tick={{
                            fontSize: 11,
                            fontFamily: `'Ubuntu', sans-serif`,
                            fill: fontColorLight
                        }}
                        tickFormatter={(val) => {
                            if (
                                typeof val === 'string' ||
                                typeof val === 'number' ||
                                val instanceof Date
                            ) {
                                return formatDateOnly(new Date(val));
                            }
                            return '';
                        }}
                    />

                    <YAxis
                        width={20}
                        axisLine={false}
                        tickLine={false}
                        tick={{
                            fontSize: 11,
                            fontFamily: `'Ubuntu', sans-serif`,
                            fill: fontColorLight
                        }}
                    />

                    <Tooltip
                        content={CustomTooltip}
                        cursor={{
                            fill: moonlight + opacity(0.6)
                        }}
                    />

                    <Bar
                        key="taskCount"
                        dataKey="taskCount"
                        stackId="a"
                        fill={blue}
                        radius={[CORNER_RADIUS, CORNER_RADIUS, 0, 0]}
                    >
                        {tasksAndAlertsData.map((entry, index) => {
                            const keys = Object.keys(entry);
                            const values = Object.values(entry);
                            const alertCountDataIndex = keys.findIndex(
                                (key) => key === 'alertCount'
                            );

                            // If the alertCount is 0, we need to round the corners
                            if (
                                alertCountDataIndex >= 0 &&
                                values[alertCountDataIndex] === 0
                            ) {
                                return (
                                    <Cell
                                        key={`cell-${index}`}
                                        opacity={getBarOpacity(index)}
                                    />
                                );
                            }
                            // Else do not round the corners
                            return (
                                <Cell
                                    key={`cell-${index}`}
                                    radius={0}
                                    opacity={getBarOpacity(index)}
                                />
                            );
                        })}
                    </Bar>
                    <Bar
                        key="alertCount"
                        dataKey="alertCount"
                        stackId="a"
                        fill={red}
                        radius={[CORNER_RADIUS, CORNER_RADIUS, 0, 0]}
                    >
                        {tasksAndAlertsData.map((_, index) => {
                            return (
                                <Cell
                                    key={`cell-${index}`}
                                    opacity={getBarOpacity(index)}
                                />
                            );
                        })}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </ChartContainer>
    );
}
